// ============================================================
// The map's modes: which one we are in, and which are on offer
// ============================================================
// The mode lives in the panel: nothing outside the map cares which one you
// are in, and threading it through the shell would be plumbing for its own
// sake. The shell hears one thing only - whether the map wants the whole
// column - through `useReportColumn`.

import { useEffect, useMemo, useState } from 'react';
import { availableModeKeys, settleMode } from '../../engine/battlefield/modes';

/**
 * TWO modes, not three - and then Deploy and Scenes.
 *
 * Measuring stopped being a mode once the camera stopped living on the
 * left button: a left-drag can mean "measure" everywhere, because nothing
 * else wants it. Build stays separate on purpose - mid-fight, a stray drag
 * must never nudge a wall someone is measuring cover against.
 */
export const MODES = [
  {
    key: 'play',
    label: 'Play',
    icon: 'play',
    hint: 'Drag to measure · click then hold to move · right-drag turns · ? for the rest',
  },
  {
    key: 'build',
    label: 'Build',
    icon: 'build',
    hint: 'Drag places · right-drag erases · ? for the rest',
    gmOnly: true,
  },
  {
    key: 'deploy',
    label: 'Deploy',
    icon: 'deploy',
    // HONESTLY. This said "Plant the marker - arrivals ring around it"
    // long after the mode grew eight gestures, which is a third of the
    // truth (v8.25.0).
    hint: 'Click plants · drag a line · alt-drag a block · right-drag recalls · ? for the rest',
    gmOnly: true,
    lock: 'deployment',
  },
  {
    key: 'scenes',
    label: 'Scenes',
    icon: 'scenes',
    hint: 'Cut between scenes · strike to name the stage · the curtain hides the map from players',
    // GM-only WITH NO LOCK, like Build: the wings are the GM's secret, and
    // there is no version of "a player cuts the table" that is not the GM's
    // job handed over whole (v8.336.0).
    gmOnly: true,
  },
];

/**
 * The mode, the modes on offer, the build option showing, and the flags
 * everything else branches on.
 *
 * WHICH BUTTONS ARE ON THE BAR - and, because the two must agree, which
 * mode we may be in. The toolbar filtered its buttons while `mode` was
 * left alone, so a GM demoted mid-Build, or a `deployment` lock flipped
 * while somebody was deploying, went on building with no Build button and
 * no way out (v8.36.0).
 */
export function usePanelModes({ modes, isGm, isLocked, modeRequest }) {
  const [mode, setMode] = useState(() => (modes?.length ? modes[0] : 'play'));
  const availableModes = useMemo(
    () => availableModeKeys(MODES, { allowed: modes, isGm, isLocked }),
    [modes, isGm, isLocked],
  );
  const availableKey = availableModes.join(',');
  useEffect(() => {
    setMode((m) => settleMode(m, availableKey ? availableKey.split(',') : []));
  }, [availableKey]);
  // A MODE ASKED FOR FROM OUTSIDE (v8.430.0): starting a fight "by hand"
  // opens the map in Deploy. Honoured once per request (its key), and only
  // for a mode this viewer is offered.
  const requestKey = modeRequest?.key;
  useEffect(() => {
    if (requestKey === undefined) return;
    if (availableKey.split(',').includes(modeRequest.mode)) setMode(modeRequest.mode);
    // The request is the trigger; availability is read as it stands then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);
  // WHICH OPTION ITEM IS SHOWING, held here rather than in the bar. With
  // `view` open the bar is not for building at all, so the build gestures
  // step aside - and a component cannot answer that about itself from the
  // outside, so the bar is controlled (v8.0.1).
  const [buildOption, setBuildOption] = useState('shape');
  const [terrainEditor, setTerrainEditor] = useState(false);
  return { mode, setMode, availableModes, buildOption, setBuildOption, terrainEditor, setTerrainEditor };
}

/**
 * The flags every gesture and every piece of furniture branches on, from
 * the mode and the build option.
 */
export function modeFlags(mode, buildOption) {
  const building = mode === 'build';
  const deploying = mode === 'deploy';
  return {
    building,
    // Building, but LOOKING rather than laying anything down. The View item
    // is not a tool: while it is showing, right-drag turns the map instead
    // of erasing and the wheel zooms, so a GM can walk round what they have
    // built without leaving Build to do it.
    looking: building && buildOption === 'view',
    deploying,
    // Scenes wants the column too, but every GESTURE stays Play's - the GM
    // places people on the stage by dragging while the rail is open
    // (v8.336.0).
    scenesMode: mode === 'scenes',
    // DOES THE MAP NEED THE WHOLE COLUMN? Build does; so does Deploy, for
    // exactly the same reason - a rail down one side, a bar along the
    // bottom, and a scene big enough to aim at (v8.23.0).
    wantsTheColumn: building || deploying || mode === 'scenes',
    // WHOEVER MAY MOVE A MARKER MAY TURN ONE - the same condition as
    // `draggable`, deliberately. Withholding the rotation while allowing
    // the move protects nothing; the GM overrules from the facing radio,
    // which IS a ruling and does need the grant (v8.57.0).
    canTurn: !building && !deploying,
  };
}

/**
 * Tell the shell whether the map wants the whole column.
 *
 * REPORTED ON EVERY CHANGE INCLUDING MOUNT, and reported false on the way
 * out of the tree. It used to skip the first run, because the shell
 * TOGGLED the log on this signal and a "not building" at mount would open a
 * log the viewer had just collapsed. Now the shell states a yield rather
 * than toggling a preference, so mount is safe to report - and reporting it
 * is what stops this panel remounting (it sits under an ErrorBoundary) from
 * leaving the log stuck shut with nothing left that remembers why (v8.36.0).
 */
export function useReportColumn(wantsTheColumn, onBuildingChange) {
  useEffect(() => {
    onBuildingChange?.(wantsTheColumn);
  }, [wantsTheColumn, onBuildingChange]);
  useEffect(() => () => onBuildingChange?.(false), [onBuildingChange]);
}
